






































































































































































import { Mixins, Prop, PropSync, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { Farm } from '@/core/interfaces/farm';
import { VehicleType } from '@/core/interfaces/vehicleType';
import { Vehicle } from '@/core/interfaces/vehicle';
import { VehiclePhase } from '@/core/interfaces/vehiclePhase';
import Component from 'vue-class-component';
import TranslatesVehiclePhase from '@/components/mixins/TranslatesVehiclePhase';
import { Ticket } from '@/core/interfaces/ticket';
import axios from 'axios';
import filtersToUrlParams from '@/core/FiltersToUrlParams';
import { Notification } from '@/core/interfaces/notification';
import { NotificationType } from '@/core/interfaces/notificationType';
import UsesNotificationMessage from '@/components/mixins/UsesNotificationMessage';
import FormatsDate from '@/components/mixins/FormatsDate';
import { formatISO, parseISO, subHours } from 'date-fns';

@Component({})
export default class RegistrationFarmAndVehicleForm extends Mixins(
    TranslatesVehiclePhase,
    UsesNotificationMessage,
    FormatsDate,
) {
    @Getter('farmsSet')
    farms!: Farm[];

    @Getter('vehicleTypeById')
    vehicleTypeById!: (id: number) => VehicleType;

    @Getter('vehiclesByFarm')
    vehiclesByFarm!: (farmId: number | null) => Vehicle[];

    @Getter('vehicleById')
    vehicleById!: (id: number | null) => Vehicle;

    @Getter('vehiclePhasesByVehicleTypeId')
    vehiclePhasesByVehicleTypeId!: (vehicleTypeId: number) => VehiclePhase[];

    @Getter('vehiclePhaseById')
    vehiclePhaseById!: (id: number) => VehiclePhase;

    @Getter('notificationTypeById')
    notificationTypeById!: (id: number) => NotificationType;

    @PropSync('farmId', { type: Number, default: null })
    farmIdSync!: number | null;

    @PropSync('vehicleId', { type: Number, default: null })
    vehicleIdSync!: number | null;

    @PropSync('vehiclePhaseId', { type: Number, default: null })
    vehiclePhaseIdSync!: number | null;

    @PropSync('notificationId', { type: Number, default: null })
    notificationIdSync!: number | null;

    @PropSync('ticketId', { type: Number, default: null })
    ticketIdSync!: number | null;

    @PropSync('noTicketOrNotification', { type: Boolean, default: false })
    noTicketOrNotificationSync!: boolean;

    @Prop({ type: Boolean })
    locked!: boolean;

    @Prop({ type: Boolean, default: true })
    showVehiclePhase!: boolean;

    @Prop({ type: String, default: () => formatISO(new Date()) })
    startedAtDate!: string;

    @Prop({ type: String, default: null })
    from!: 'ticket' | 'notification' | null;

    @Prop({ type: Object, default: null })
    selectedTicket!: Ticket | null;

    @Prop({ type: Object, default: null })
    selectedNotification!: Notification | null;

    notifications: Notification[] = [];
    tickets: Ticket[] = [];

    @Watch('farmIdSync')
    setFarm() {
        if (!this.farmIdSync) {
            this.vehicleIdSync = null;
        }

        if (!this.vehiclesFiltered.length) {
            this.vehicleIdSync = null;
            return;
        }

        if (
            !this.vehicleIdSync ||
            !this.vehiclesFiltered.map((v) => v.id).includes(this.vehicleIdSync)
        ) {
            this.vehicleIdSync = this.vehiclesFiltered[0].id;
        }
    }

    @Watch('vehicleIdSync', { immediate: true })
    setVehicle() {
        if (
            !this.vehiclePhaseIdSync ||
            !this.phaseOptions
                .map((p) => p.id)
                .includes(this.vehiclePhaseIdSync)
        ) {
            this.vehiclePhaseIdSync = null;
        }
    }

    @Watch('vehicleIdSync')
    @Watch('startedAtDate', { immediate: true })
    loadNotifications(): void {
        if (this.vehicleIdSync) {
            this.fetchTickets(this.vehicleIdSync);
            this.fetchNotifications(this.vehicleIdSync);
        }
    }

    get vehicleTypeId(): number | null {
        if (!this.vehicleIdSync) {
            return null;
        }

        return this.vehicleById(this.vehicleIdSync)?.vehicle_type_id ?? null;
    }

    get vehiclesFiltered(): { label: string; id: number }[] {
        if (!this.farmIdSync) {
            return [];
        }

        return this.vehiclesByFarm(this.farmIdSync)
            .map((vehicle) => ({
                label: `${
                    this.vehicleTypeById(vehicle.vehicle_type_id).name
                } - ${vehicle.name}`,
                id: vehicle.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    get phaseOptions(): { label: string; id: number }[] {
        if (!this.vehicleTypeId) {
            return [];
        }

        return this.vehiclePhasesByVehicleTypeId(this.vehicleTypeId)
            .map((phase) => ({
                label: `${phase.code_name} - ${this.translateVehiclePhase(
                    phase,
                )}`,
                id: phase.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    async fetchNotifications(vehicleId: number): Promise<void> {
        this.notifications = (
            await axios.get<{ notifications: Notification[] }>(
                '/notifications',
                {
                    params: {
                        ...filtersToUrlParams({
                            vehicleIds: [vehicleId],
                            dateFrom: subHours(
                                parseISO(this.startedAtDate),
                                24,
                            ).toISOString(),
                            dateUntil: this.startedAtDate,
                        }),
                        limit: 100,
                    },
                },
            )
        ).data.notifications;
    }

    async fetchTickets(vehicleId: number): Promise<void> {
        const urls = ['/tickets', '/tickets/closed'];

        const responses = await Promise.all(
            urls.map((url) =>
                axios.get<Ticket[]>(url, {
                    params: {
                        ...filtersToUrlParams({
                            vehicleIds: [vehicleId],
                            dateFrom: subHours(
                                parseISO(this.startedAtDate),
                                24,
                            ).toISOString(),
                            dateUntil: parseISO(this.startedAtDate),
                        }),
                        limit: 100,
                    },
                }),
            ),
        );

        this.tickets = responses
            .map((response) => response.data)
            .flat()
            .sort(
                (a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );
    }

    get vehicle(): Vehicle | null {
        if (!this.vehicleIdSync) {
            return null;
        }

        return this.vehicleById(this.vehicleIdSync);
    }

    get vehicleType(): VehicleType | null {
        if (!this.vehicle) {
            return null;
        }

        return this.vehicleTypeById(this.vehicle.vehicle_type_id);
    }

    get notificationOptions(): { label: string; id: number }[] {
        return this.notifications.map((notification: Notification) => ({
            id: notification.id,
            label: this.notificationLabel(notification),
        }));
    }

    get ticketOptions(): { label: string; id: number }[] {
        return this.tickets.map((ticket: Ticket) => ({
            id: ticket.id,
            label: `${this.formatDate(ticket.created_at)} - ${ticket.subject} ${ticket.closed_at ? ' 🔒' : ''}`,
        }));
    }

    notificationLabel(notification: Notification): string {
        return `${this.formatDate(
            notification.created_at,
        )} - ${this.translateNotificationMessage(
            this.notificationTypeById(notification.notification_type_id),
            this.vehicleType!,
            notification,
        )} - ${this.translateVehiclePhase(
            this.vehiclePhaseById(notification.vehicle_phase_id),
        )}`;
    }

    get selectedNotificationLabel(): string {
        if (!this.selectedNotification) {
            return '';
        }

        return this.notificationLabel(this.selectedNotification);
    }

    get selectedTicketLabel(): string {
        if (!this.selectedTicket) {
            return '';
        }

        return `${this.selectedTicket.code}: ${this.selectedTicket.subject}`;
    }

    get selectedFarm(): string {
        return this.farms.find((f) => f.id === this.farmIdSync)?.name || '';
    }

    get selectedVehicle(): string {
        return (
            this.vehiclesByFarm(this.farmIdSync).find(
                (v) => v.id === this.vehicleIdSync,
            )?.name || ''
        );
    }

    get selectedVehiclePhase(): string {
        return this.vehiclePhaseById(this.vehiclePhaseIdSync!)?.code_name || '';
    }
}
